import React, { useState } from 'react';
import { Layout, Menu, ConfigProvider, Button, Drawer, Modal, Input,Popconfirm } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { ApiOutlined, AuditOutlined, BookOutlined, ContactsOutlined, DeliveredProcedureOutlined, MailOutlined, MenuOutlined, PrinterOutlined, QuestionCircleOutlined, SolutionOutlined } from '@ant-design/icons';
import {
  UserOutlined,
  SettingOutlined,
  DashboardOutlined,
  HistoryOutlined,
  ProfileOutlined,
  AreaChartOutlined,
  SafetyCertificateOutlined,
  FlagOutlined,
  PaperClipOutlined,
  ControlOutlined,
  EyeOutlined,
  TeamOutlined,
  FundOutlined,
  ProductOutlined,
  AppstoreOutlined,
  SendOutlined,
  ApartmentOutlined,
  VideoCameraOutlined,
  GlobalOutlined,
  VideoCameraAddOutlined,
  WifiOutlined,
  NotificationOutlined,
  ContainerOutlined,
  LogoutOutlined,
  LineChartOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useLogout } from '../../utils';
import { useCustomerLogoStore, useSmallCustomerLogoStore, useRoleNameStore, useMobileStore, useThemeStore, useIsSuperAdmin, usePaginationStore } from '../../store';
import ThemeButton from '../../components/ThemeButton/ThemeButton';
import QuestionModal from '../CRUDUtils/QuestionModal';
import EmailModal from '../CRUDUtils/EmailModal';
import RestartModal from '../CRUDUtils/RestartModal';

const { Sider, Header } = Layout;

export default function Sidebar({ collapsed, onCollapse }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const roleName = useRoleNameStore((state) => state.roleName);
  const [modalVisible, setModalVisible] = useState(false);
  const [restartModalVisible, setRestartModalVisible] = useState(false)
  const [bottomMenuSelectedKey, setBottomMenuSelectedKey] = useState('')
  const [emailerModalVisible, setEmaierlModalVisible] = useState(false)
  const customerLogo = useCustomerLogoStore((state) => state.customerLogo);
  const smallCustomerLogo = useSmallCustomerLogoStore((state) => state.smallCustomerLogo);
  const isSuperAdmin = useIsSuperAdmin();
  const isMobile = useMobileStore((state) => state.isMobile);
  const location = useLocation();
  const updatePagination = usePaginationStore((state) => state.updatePagination);
  const logout = useLogout();
  const confirmDelete = () => {
    Modal.confirm({
      title: 'Delete item?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      onOk: () => {
        console.log('deleted');
      },
    });
  };

  // Menu items with support for unlimited nested children
  const menuItems = [
    {
      key: 'events',
      label: 'Events',
      icon: <SafetyCertificateOutlined />,
      path: '/events',
      roles: ['user', 'administrator', 'super administrator', 'read-only', 'customer success', 'validator'],
    },
    {
      key: 'deployments',
      label: 'Deployments',
      icon: <DashboardOutlined />,
      path: '/deployments',
      roles: ['user', 'administrator', 'super administrator', 'read-only', 'customer success'],
    },
    {
      key: 'old_deployments',
      label: 'Old Deployments',
      icon: <HistoryOutlined />,
      path: '/old_deployments',
      roles: ['super administrator'],
    },
    {
      key: 'users',
      label: 'Users',
      icon: <UserOutlined />,
      path: '/users',
      roles: ['user', 'administrator', 'super administrator', 'read-only', 'customer success'],
    },
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <AreaChartOutlined />,
      path: '/dashboard',
      roles: ['user', 'administrator', 'super administrator', 'read-only', 'customer success'],
      children: [
        {
          key: 'externalDashboard',
          label: 'External',
          icon: <AreaChartOutlined />,
          path: '/dashboard',
          roles: ['super administrator'],
        },
        {
          key: 'internalDashboard',
          label: 'Internal',
          icon: <LineChartOutlined />,
          path: '/internaldashboard',
          roles: ['super administrator'],
          exclude_mobile: false,
        },
      ]
    },
    {
      key: 'profile',
      label: 'Profile',
      icon: <ProfileOutlined />,
      path: '/profile',
      roles: ['user', 'administrator', 'super administrator', 'read-only', 'customer success', 'validator'],
    },
    {
      key: 'globe',
      label: 'Satellite View',
      icon: <GlobalOutlined />,
      path: '/globe',
      roles: ['user', 'administrator', 'super administrator'],
      exclude_mobile: false,
    },
    {
      key: 'management',
      label: 'Management',
      icon: <SettingOutlined />,
      roles: ['super administrator'],
      children: [
        {
          key: 'clients',
          label: 'Tenancy',
          icon: <TeamOutlined/>,
          roles: ['super administrator'],
          children: [
            { 
              key: 'customers',
              label: 'Customers',
              icon: <SolutionOutlined/>,
              path: '/customers',
              roles: ['super administrator']
            },
            { 
              key: 'logos',
              label: 'Logos',
              icon: <ContactsOutlined/>,
              path: '/logos',
              roles: ['super administrator']
            },
          ]
        },
        {
          key: 'detection-types',
          label: 'Detection Types',
          icon: <EyeOutlined />,
          path: '/detection-types',
          roles: ['super administrator'],
        },
        {
          key: 'detection-packages',
          label: 'Detection Packages',
          icon: <ContainerOutlined />,
          path: '/detection-packages',
          roles: ['super administrator'],
        },
        {
          key: 'email-control',
          label: 'Emails',
          icon: <BookOutlined/>,
          roles: ['super administrator'],
          children: [
            { 
              key: 'email_chains',
              label: 'Chains',
              icon: <AuditOutlined/>,
              path: '/email-chains',
              roles: ['super administrator']
            },
            { 
              key: 'email_tracking',
              label: 'Tracking',
              icon: <SendOutlined/>,
              path: '/email-tracking',
              roles: ['super administrator']
            },
            { 
              key: 'email_formats',
              label: 'Formats',
              icon: <PrinterOutlined/>,
              path: '/email-formats',
              roles: ['super administrator']
            },
          ]
        },
        {
          key: 'hardware',
          label: 'Hardware',
          icon: <ControlOutlined />,
          //path: '/hardware',
          roles: ['super administrator'],
          children: [
            {
              key: 'hardware_info',
              label: 'Inventory',
              icon: <AppstoreOutlined />,
              path: '/hardware-info',
              roles: ['super administrator'],
            },
            {
              key: 'hardware_type',
              label: 'Types',
              icon: <ProductOutlined />,
              path: '/hardware-types',
              roles: ['super administrator'],
            },
            {
              key: 'data',
              label: 'Protocols',
              icon: <VideoCameraOutlined />,
              path: '/data-types',
              roles: ['super administrator'],
            },
            {
              key: 'stream_resolutions',
              label: 'Resolutions',
              icon: <VideoCameraAddOutlined />,
              path: '/stream-resolutions',
              roles: ['super administrator'],
            },
            {
              key: 'credentials',
              label: 'Credentials',
              icon: <KeyOutlined />,
              path:'/credentials',
              roles: ['super administrator']
            },
            // {
            //   key: 'notes',
            //   label: 'Notes',
            //   icon: <ContainerOutlined/>,
            //   path:'/notes',
            //   roles: ['super administrator']
            // }
          ]
        },
        {
          key: 'locations',
          label: 'Locations',
          icon: <FlagOutlined />,
          path: '/locations',
          roles: ['super administrator'],
        },
        {
          key: 'projects',
          label: 'Projects',
          icon: <PaperClipOutlined />,
          path: '/projects',
          roles: ['super administrator'],
        },
        {
          key: 'weekly_report',
          label: 'Weekly Report',
          icon: <ApartmentOutlined/>,
          path: '/weekly-report',
          roles: ['super administrator']
        },
        {
          key: 'configs',
          label: 'Configs',
          icon: <ProfileOutlined/>,
          path: '/configs',
          roles: ['super administrator']
        }
      ],
    },
  ];

  // Recursive function to filter menu items based on role
  const filterItemsByRole = (items) => {
    return items
      .map(item => {
        // Check if item has children
        if (item.children) {
          // Recursively filter children
          const filteredChildren = filterItemsByRole(item.children);

          // If no children pass the filter, remove children but keep parent if it matches role
          if (filteredChildren.length === 0) {
            if (!item.roles || item.roles.includes(roleName)) {
              const { children, ...itemWithoutChildren } = item;
              return itemWithoutChildren;
            }
            return null;
          }

          return { ...item, children: filteredChildren };
        }

        // Filter leaf items by role

        if (item.exclude_mobile !== undefined && isMobile) return null

        if (!item.roles || item.roles.includes(roleName))return item;
        return null;
      })
      .filter(Boolean);
  };

  // Recursive function to render menu items with nested submenus
  const renderMenuItems = (items) => {
    return items.map((item) => {
      // If item has children, render as SubMenu
      if (item.children) {
        // Remove path property from dashboard if user is super admin
        const itemToRender = item.key === 'dashboard' && isSuperAdmin 
          ? { ...item, path: undefined } 
          : item;

        return (
          <Menu.SubMenu key={itemToRender.key} icon={itemToRender.icon} title={itemToRender.label}>
            {renderMenuItems(itemToRender.children)}
          </Menu.SubMenu>
        );
      }

      // Render leaf item as Menu.Item
      return (
        <Menu.Item key={item.path} icon={item.icon}>
          <NavLink
            to={item.path}
            onClick={() => {
              updatePagination({ current: 1, pageSize: 10, total: 10 })
            }}
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            {item.label}
          </NavLink>
        </Menu.Item>
      );
    });
  };

  // Convert menu items for horizontal display
  const convertMenuItemsForHeader = (items) => {
    return items.map((item) => {
      if (item.children) {
        return {
          key: item.key,
          label: item.label,
          icon: item.icon,
          children: convertMenuItemsForHeader(item.children),
        };
      }

      return {
        key: item.path,
        label: (
          <NavLink
            to={item.path}
            className={({ isActive }) => (isActive ? 'active' : '')}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {item.label}
          </NavLink>
        ),
        icon: item.icon,
      };
    });
  };

  // Filter items based on role
  const filteredItems = filterItemsByRole(menuItems);
  const headerMenuItems = convertMenuItemsForHeader(filteredItems);
  // Add logout at the end
  headerMenuItems.push({
    key: 'logout',
    label: 'Logout',
    icon: <LogoutOutlined />,
    onClick: logout,
  });

  // Hide Sider on mobile
  if (isMobile) {
    return (
      <>
        <Header className={`mobile-header dark-theme`}>
          <div className="mobile-header-logo">
            <img
              src={`${process.env.REACT_APP_DOWNLOAD_URL}/assets/logo?logo_id=${customerLogo}&token=${localStorage.getItem('token')}`}
              alt="Logo"
            />
            <h1 className='nav-title mobile'>Orbital Site Monitoring</h1>
          </div>

          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuOpen(true)}
            className="mobile-menu-button"
          />
        </Header>

        <Drawer
          title="Menu"
          placement="right"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          className={`mobile-drawer dark-theme`}
        >

          <ConfigProvider
            theme={{
              components: {
                Menu: {
                  darkItemSelectedBg: '#001529',   // dark mode selected bg
                  darkItemSelectedColor: '#ffffff',
                  darkItemHoverBg: '#002B53'
                },
              },
            }}
          >

            <Menu
              theme='dark'
              mode="inline"
              selectedKeys={[location.pathname]}
              onClick={() => setMobileMenuOpen(false)}
            >
              {renderMenuItems(filteredItems)}
              <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={logout}>
                Logout
              </Menu.Item>
              <Menu.Item key="help" icon={<QuestionCircleOutlined />} onClick={() => setModalVisible(true)}>
                Help
              </Menu.Item>
            </Menu>
          </ConfigProvider>
        </Drawer>

        <QuestionModal open={modalVisible} onClose={() => {
          setModalVisible(false);
          setBottomMenuSelectedKey('')
        }}>
        </QuestionModal>

        {isSuperAdmin &&
          <EmailModal open={emailerModalVisible} onClose={() => {
            setEmaierlModalVisible(false)
            setBottomMenuSelectedKey('')
          }}>
          </EmailModal>
        }
      </>
    );
  }

  return (
    <>
    <Sider collapsible collapsed={collapsed} onCollapse={onCollapse} theme='dark' className="sidebar-sider">
      <div className="sidebar-menu-container">
        {/* Logo */}
        <div className='sidebar-menu-top'>
          <div className="sidebar-logo">
            <img
              src={collapsed ? `${process.env.REACT_APP_DOWNLOAD_URL}/assets/logo?logo_id=${smallCustomerLogo}&token=${localStorage.getItem('token')}`:   `${process.env.REACT_APP_DOWNLOAD_URL}/assets/logo?logo_id=${customerLogo}&token=${localStorage.getItem('token')}`}
              alt="Logo"
              className={collapsed ? 'collapsed' : ''}
            />
          </div>


        <ConfigProvider
            theme={{
              components: {
                Menu: {
                  darkItemSelectedBg: '#001529',   // dark mode selected bg
                  darkItemSelectedColor: '#ffffff',
                  darkItemHoverBg: '#002f5d'
                },
              },
            }}
          >

          {/* Menu */}
          <Menu theme='dark' mode="inline" selectedKeys={[location.pathname]}>
            {renderMenuItems(filteredItems)}
          </Menu>
          </ConfigProvider>
        </div>
        <div className='sidebar-menu-bottom'>
          {/* Bottom link */}

          <ConfigProvider
            theme={{
              components: {
                Menu: {
                  darkItemSelectedBg: '#001529',   // dark mode selected bg
                  darkItemSelectedColor: '#ffffff',
                  darkItemHoverBg: '#002f5d'
                },
              },
            }}
          >

            <Menu className='logout-button' theme='dark' mode="inline" selectedKeys={bottomMenuSelectedKey}>
              {isSuperAdmin &&
              
                <Menu.Item key="emailer" icon={<MailOutlined/>} onClick={() => setEmaierlModalVisible(true)}>
                  Emailer
                </Menu.Item>
              }
              {isSuperAdmin &&
                <Menu.Item key="restart" icon={<ApiOutlined/>} onClick={() => setRestartModalVisible(true)}>
                  Refresh VMS
                </Menu.Item>
              }
              <Menu.Item key="help" icon={<QuestionCircleOutlined />} onClick={() => setModalVisible(true)}>
                Help
              </Menu.Item>
              <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={logout}>
                Logout
              </Menu.Item>
            </Menu>
          </ConfigProvider>
          
        </div>
      </div>
    </Sider>

    <RestartModal open={restartModalVisible} onClose={() => {
      setRestartModalVisible(false);
      setBottomMenuSelectedKey('');
      }}>
    </RestartModal>

    <QuestionModal open={modalVisible} onClose={() => {
      setModalVisible(false);
      setBottomMenuSelectedKey('')
      }}>
    </QuestionModal>

    {isSuperAdmin &&
      <EmailModal open={emailerModalVisible} onClose={() => {
        setEmaierlModalVisible(false)
        setBottomMenuSelectedKey('')
      }}>
      </EmailModal>
    }

    </>
  );
}